// CodeMirror 6 markdown editor with vault-aware completion.
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import { EditorState } from '@codemirror/state';
import { EditorView, drawSelection, highlightActiveLine, keymap } from '@codemirror/view';
import { basename, stripMarkdownExt } from './dom.js';

/** Markdown highlighting that follows the CSS variables (light/dark themes). */
const markdownHighlight = HighlightStyle.define([
  { tag: t.heading, color: 'var(--cm-heading)', fontWeight: '650' },
  { tag: t.strong, color: 'var(--cm-strong)', fontWeight: '650' },
  { tag: t.emphasis, color: 'var(--cm-emphasis)', fontStyle: 'italic' },
  { tag: t.link, color: 'var(--cm-link)', textDecoration: 'underline' },
  { tag: t.url, color: 'var(--cm-link)' },
  { tag: t.monospace, color: 'var(--cm-code)', background: 'var(--code-bg)' },
  { tag: t.quote, color: 'var(--cm-quote)', fontStyle: 'italic' },
  { tag: t.list, color: 'var(--cm-list)' },
  { tag: t.contentSeparator, color: 'var(--cm-rule)' },
  { tag: t.meta, color: 'var(--cm-meta)' },
  { tag: t.processingInstruction, color: 'var(--muted)' },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.invalid, color: 'var(--danger)' },
]);

export interface EditorCallbacks {
  onChange(): void;
  onBlur(): void;
  onSave(): void;
  onQuickOpen(): void;
  onSearch(): void;
  wikiTargets(): string[];
  tagNames(): string[];
}

const WIKILINK_BEFORE = /\[\[[^\]\n|]*$/;
const TAG_BEFORE = /(?:^|[\s(])#([A-Za-z0-9_\-/]*)$/;

function wikilinkSource(callbacks: EditorCallbacks) {
  return (context: CompletionContext): CompletionResult | null => {
    const before = context.matchBefore(WIKILINK_BEFORE);
    if (!before) return null;
    const typed = before.text.slice(2).toLowerCase();
    const options: Completion[] = [];
    for (const path of callbacks.wikiTargets()) {
      if (!/\.(md|markdown)$/i.test(path)) continue;
      const label = stripMarkdownExt(basename(path));
      if (typed && !label.toLowerCase().includes(typed) && !path.toLowerCase().includes(typed)) continue;
      options.push({
        label,
        detail: path,
        type: 'class',
        apply: (view, completion, from, to) => {
          const text = `${completion.label}]]`;
          view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length } });
        },
      });
      if (options.length >= 300) break;
    }
    if (options.length === 0) return null;
    return { from: before.from + 2, options, validFor: /^[^\]\n|]*$/ };
  };
}

function tagSource(callbacks: EditorCallbacks) {
  return (context: CompletionContext): CompletionResult | null => {
    const before = context.matchBefore(TAG_BEFORE);
    if (!before) return null;
    const typed = before.text.replace(/^[\s(]/, '');
    const options: Completion[] = callbacks
      .tagNames()
      .map((tag) => ({ label: `#${tag}`, type: 'keyword' }))
      .filter((option) => typed === '#' || option.label.startsWith(typed));
    if (options.length === 0) return null;
    const prefix = before.text.startsWith('#') ? 0 : 1;
    return { from: before.from + prefix, options, validFor: /^#[A-Za-z0-9_\-/]*$/ };
  };
}

/** Editor wrapper: owns the CodeMirror view and its key bindings. */
export class MarkdownEditor {
  readonly view: EditorView;
  private readonly container: HTMLElement;
  private suppressChange = false;

  constructor(parent: HTMLElement, callbacks: EditorCallbacks) {
    this.container = parent;
    const keymapExtension = keymap.of([
      { key: 'Mod-s', preventDefault: true, run: () => (callbacks.onSave(), true) },
      { key: 'Mod-o', preventDefault: true, run: () => (callbacks.onQuickOpen(), true) },
      { key: 'Mod-Shift-f', preventDefault: true, run: () => (callbacks.onSearch(), true) },
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...historyKeymap,
      indentWithTab,
    ]);
    this.view = new EditorView({
      parent,
      state: EditorState.create({
        doc: '',
        extensions: [
          history(),
          drawSelection(),
          highlightActiveLine(),
          // `[`/`]` are excluded from auto-closing so `[[wikilinks]]` can be typed
          // literally; the completion source inserts the closing `]]` on accept.
          EditorState.languageData.of(() => [{ closeBrackets: { brackets: '(){}' + "''" + '""' + '``' } }]),
          closeBrackets(),
          EditorView.lineWrapping,
          markdown({ base: markdownLanguage }),
          syntaxHighlighting(markdownHighlight),
          autocompletion({
            override: [wikilinkSource(callbacks), tagSource(callbacks)],
            activateOnTyping: true,
            maxRenderedOptions: 60,
          }),
          keymapExtension,
          EditorView.updateListener.of((update) => {
            if (update.docChanged && !this.suppressChange) callbacks.onChange();
          }),
          EditorView.domEventHandlers({ blur: () => callbacks.onBlur() }),
        ],
      }),
    });
  }

  /** Replaces the document without firing a change notification. */
  setDoc(text: string): void {
    this.suppressChange = true;
    this.view.dispatch({
      changes: { from: 0, to: this.view.state.doc.length, insert: text },
      selection: { anchor: 0 },
    });
    this.suppressChange = false;
  }

  getDoc(): string {
    return this.view.state.doc.toString();
  }

  focus(): void {
    this.view.focus();
  }

  setVisible(visible: boolean): void {
    this.container.hidden = !visible;
  }

  scrollToTop(): void {
    this.view.scrollDOM.scrollTop = 0;
  }

  destroy(): void {
    this.view.destroy();
  }
}
